import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { get, post, put, errorMessage } from '@/shared/api/client';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Skeleton, Table } from '@/shared/ui';
import { useToast } from '@/app/providers';
import { useSession } from '@/shared/api/session';
import { formatDate } from '@/shared/lib/format';

type User = { id: number; email: string; full_name: string; rut: string | null; is_active: boolean; must_change_password: boolean; created_at: string; version: number; roles: string[]; mfa_enabled: boolean; direct_permissions: string[] };
type Page = { items: User[]; total: number; page: number; page_size: number };
type Role = { code: string; name: string; requires_mfa: boolean };

const ROLE_OPTIONS = ['BENEFICIARIO', 'COORDINADOR', 'FINANZAS', 'GERENCIA', 'SUPERADMIN'];
const schema = z.object({ email: z.email('Correo inválido'), full_name: z.string().min(2, 'Nombre requerido'), rut: z.string().optional(), roles: z.array(z.string()).min(1, 'Selecciona al menos un rol') });
type Form = z.infer<typeof schema>;

export function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [rolesFor, setRolesFor] = useState<User | null>(null);
  const users = useQuery({ queryKey: ['admin', 'users', q], queryFn: () => get<Page>(`/admin/users?page_size=50${q ? `&q=${encodeURIComponent(q)}` : ''}`) });
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: () => get<Role[]>('/admin/roles') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  const create = useMutation({ mutationFn: (v: Form) => post<User>('/admin/users', v, true), onSuccess: (u) => { invalidate(); setOpen(false); toast.notify({ title: 'Usuario creado', description: `Se envió la invitación a ${u.email}` }); } });
  const toggle = useMutation({ mutationFn: (u: User) => post<User>(`/admin/users/${u.id}/${u.is_active ? 'deactivate' : 'reactivate'}`, { reason: null }), onSuccess: invalidate, onError: (e) => toast.notify({ title: 'No se pudo cambiar el estado', description: errorMessage(e), kind: 'error' }) });
  const resend = useMutation({ mutationFn: (u: User) => post<void>(`/admin/users/${u.id}/resend-invitation`), onSuccess: () => toast.notify({ title: 'Invitación reenviada' }), onError: (e) => toast.notify({ title: 'No se pudo reenviar', description: errorMessage(e), kind: 'error' }) });
  const setRoles = useMutation({ mutationFn: ({ u, roles }: { u: User; roles: string[] }) => put<User>(`/admin/users/${u.id}/roles`, { roles }), onSuccess: invalidate, onError: (e) => toast.notify({ title: 'No se pudieron cambiar los roles', description: errorMessage(e), kind: 'error' }) });
  const grant = useMutation({ mutationFn: (u: User) => post<User>(`/admin/users/${u.id}/permissions`, { permission: 'fxr:solicitar' }), onSuccess: invalidate });
  const { register, handleSubmit, formState: { errors }, reset } = useForm<Form>({ resolver: zodResolver(schema), defaultValues: { roles: ['BENEFICIARIO'] } });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)', flexWrap: 'wrap' }}>
        <h1>Usuarios</h1>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <Input placeholder="Buscar por nombre o correo" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }} aria-label="Buscar usuarios" />
          <Dialog.Root open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); create.reset(); } }}>
            <Dialog.Trigger asChild><Button>Nuevo usuario</Button></Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 40 }} />
              <Dialog.Content aria-describedby={undefined} style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(480px, 92vw)', zIndex: 41 }}>
                <Card title="Nuevo usuario">
                  <Dialog.Title className="sr-only">Nuevo usuario</Dialog.Title>
                  {create.isError && <Alert kind="error">{errorMessage(create.error)}</Alert>}
                  <form onSubmit={handleSubmit((v) => create.mutate(v))} noValidate>
                    <Field label="Correo corporativo" htmlFor="u-email" error={errors.email?.message}><Input id="u-email" type="email" invalid={!!errors.email} {...register('email')} /></Field>
                    <Field label="Nombre completo" htmlFor="u-name" error={errors.full_name?.message}><Input id="u-name" invalid={!!errors.full_name} {...register('full_name')} /></Field>
                    <Field label="RUT (opcional)" htmlFor="u-rut" hint="Formato 12345678-9"><Input id="u-rut" {...register('rut')} /></Field>
                    <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--sp-4)' }}>
                      <legend style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, marginBottom: 'var(--sp-2)' }}>Roles</legend>
                      {ROLE_OPTIONS.map((r) => (
                        <label key={r} style={{ display: 'flex', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)', padding: '4px 0' }}>
                          <input type="checkbox" value={r} {...register('roles')} /> {r}{roles.data?.find((x) => x.code === r)?.requires_mfa ? ' (requiere MFA)' : ''}
                        </label>
                      ))}
                      {errors.roles && <div role="alert" style={{ color: 'var(--sgr-danger)', fontSize: 'var(--fs-xs)' }}>{errors.roles.message}</div>}
                    </fieldset>
                    <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>Se generará una contraseña temporal (72 h) y se enviará por correo. El usuario deberá cambiarla en su primer acceso.</p>
                    <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
                      <Dialog.Close asChild><Button variant="secondary" type="button">Cancelar</Button></Dialog.Close>
                      <Button type="submit" loading={create.isPending}>Crear y enviar invitación</Button>
                    </div>
                  </form>
                </Card>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </div>
      </div>
      <Card>
        {users.isPending && <><Skeleton height={20} /><div style={{ height: 8 }} /><Skeleton height={20} /></>}
        {users.isError && <Alert kind="error">{errorMessage(users.error)}</Alert>}
        {users.data && users.data.items.length === 0 && <EmptyState title="Sin usuarios" description="Crea el primer usuario con el botón superior." />}
        {users.data && users.data.items.length > 0 && (
          <Table>
            <thead><tr><th>Nombre</th><th>Correo</th><th>Roles</th><th>Estado</th><th>Alta</th><th></th></tr></thead>
            <tbody>
              {users.data.items.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name}{u.rut ? <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>{u.rut}</div> : null}</td>
                  <td>{u.email}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>{u.roles.map((r) => <Badge key={r} tone={r === 'SUPERADMIN' ? 'review' : 'neutral'}>{r}</Badge>)}{u.direct_permissions.map((p) => <Badge key={p} tone="ok">{p}</Badge>)}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Badge tone={u.is_active ? 'ok' : 'rejected'}>{u.is_active ? 'Activo' : 'Desactivado'}</Badge>
                      {u.must_change_password && <Badge tone="pending">Temporal</Badge>}
                      {u.mfa_enabled && <Badge tone="review">MFA</Badge>}
                    </div>
                  </td>
                  <td className="num">{formatDate(u.created_at)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {u.must_change_password && u.is_active && <Button variant="ghost" size="sm" onClick={() => resend.mutate(u)}>Reenviar invitación</Button>}
                    {!u.direct_permissions.includes('fxr:solicitar') && !u.roles.includes('COORDINADOR') && <Button variant="ghost" size="sm" onClick={() => grant.mutate(u)} title="Permiso fxr:solicitar">Permitir solicitar FXR</Button>}
                    <Button variant="ghost" size="sm" onClick={() => setRolesFor(u)}>Roles</Button>
                    <Button variant={u.is_active ? 'ghost' : 'secondary'} size="sm" onClick={() => { if (window.confirm(`${u.is_active ? 'Desactivar' : 'Reactivar'} a ${u.full_name}?`)) toggle.mutate(u); }}>{u.is_active ? 'Desactivar' : 'Reactivar'}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {rolesFor && <RolesDialog user={rolesFor} roles={roles.data ?? []} busy={setRoles.isPending} onClose={() => setRolesFor(null)} onSave={(codes) => setRoles.mutate({ u: rolesFor, roles: codes }, { onSuccess: () => { setRolesFor(null); toast.notify({ title: 'Roles actualizados', description: codes.join(', ') }); } })} />}
    </>
  );
}


/** Edición de roles con casillas (QA-007): solo roles válidos del catálogo, sin texto libre; SUPERADMIN protegido (no puede quitarse a sí mismo; el backend lo exige). */
export function RolesDialog({ user, roles, busy, onClose, onSave }: { user: User; roles: Role[]; busy: boolean; onClose: () => void; onSave: (codes: string[]) => void }) {
  const { data: session } = useSession();
  const [sel, setSel] = useState<string[]>(user.roles);
  const isSelf = session?.user.id === user.id;
  const options = roles.length ? roles : ROLE_OPTIONS.map((code) => ({ code, name: code, requires_mfa: false }));
  const toggle = (code: string, on: boolean) => setSel((cur) => (on ? [...cur.filter((c) => c !== code), code] : cur.filter((c) => c !== code)));
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 40 }} />
        <Dialog.Content aria-describedby={undefined} style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(440px, 92vw)', maxHeight: '90vh', overflow: 'auto', zIndex: 41 }}>
          <Card title={`Roles de ${user.full_name}`}>
            <Dialog.Title className="sr-only">Roles de {user.full_name}</Dialog.Title>
            <form onSubmit={(e) => { e.preventDefault(); if (sel.length) onSave(sel); }}>
              <div role="group" aria-label="Roles" style={{ display: 'grid', gap: 4 }}>
                {options.map((r) => {
                  const locked = r.code === 'SUPERADMIN' && isSelf && sel.includes('SUPERADMIN');
                  return (
                    <label key={r.code} style={{ display: 'flex', gap: 10, alignItems: 'center', minHeight: 44, fontSize: 'var(--fs-sm)' }}>
                      <input type="checkbox" checked={sel.includes(r.code)} disabled={locked} onChange={(e) => toggle(r.code, e.target.checked)} />
                      <span>{r.code}{r.name && r.name !== r.code ? ` — ${r.name}` : ''}{r.requires_mfa ? ' (requiere MFA)' : ''}{locked ? ' · no puedes quitarte este rol' : ''}</span>
                    </label>
                  );
                })}
              </div>
              {sel.length === 0 && <div role="alert" style={{ color: 'var(--sgr-danger)', fontSize: 'var(--fs-xs)' }}>Selecciona al menos un rol.</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 'var(--sp-3)', flexWrap: 'wrap' }}>
                <Button type="submit" loading={busy} disabled={sel.length === 0 || [...sel].sort().join() === [...user.roles].sort().join()}>Guardar roles</Button>
                <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
              </div>
            </form>
          </Card>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
